import { Handle, type NodeProps, Position } from '@xyflow/react';
import { css } from 'aphrodite';
import { useEffect, useState } from 'react';
import type { BehaviorCaseNodeData } from '../types/stepNodes';
import { behaviorCaseNodeStyles as styles } from './behaviorCaseNodeStyles';

interface BehaviorCaseNodeProps extends NodeProps {
	data: BehaviorCaseNodeData;
}

function BehaviorIcon() {
	return (
		<svg width='22' height='17' viewBox='0 0 22 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
			<path
				fillRule='evenodd'
				clipRule='evenodd'
				d='M18.3184 6.89084L21.7638 3.44542L18.3184 0L17.0041 1.31422L18.2059 2.51598H15.8024C12.1729 2.51598 11.2082 3.89992 10.3577 5.12119C9.59467 6.217 8.93477 7.16317 5.57857 7.16317H0.00195312V9.02204H5.57857C8.93477 9.02204 9.59467 9.96821 10.3577 11.064C11.2082 12.2853 12.1729 13.6692 15.8024 13.6692H18.2059L17.0041 14.871L18.3184 16.1852L21.7638 12.7398L18.3184 9.29436L17.0041 10.6086L18.2059 11.8103H15.8024C13.1433 11.8103 12.6144 11.0519 11.8829 10.0017C11.4387 9.36314 10.9386 8.64655 9.99711 8.0926C10.9386 7.53866 11.4387 6.82206 11.8829 6.18354C12.6144 5.13328 13.1433 4.37486 15.8024 4.37486H18.2059L17.0041 5.57662L18.3184 6.89084Z'
				fill='white'
			/>
		</svg>
	);
}

function ChevronIcon({ direction }: { direction: 'up' | 'down' }) {
	const path = direction === 'up' ? 'M12 6L7 1L2 6' : 'M2 2L7 7L12 2';
	return (
		<svg width='14' height='8' viewBox='0 0 14 8' fill='none'>
			<path d={path} stroke='#9198A4' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

export function BehaviorCaseNode({ data, selected }: BehaviorCaseNodeProps) {
	const [isExpanded, setIsExpanded] = useState(data.isExpanded ?? true);

	useEffect(() => {
		setIsExpanded(data.isExpanded ?? true);
	}, [data.isExpanded]);

	const handleToggleExpand = () => {
		setIsExpanded(prev => !prev);
	};

	const title = data.caseName || data.label;
	const isDefault = data.isTimeout || data.isDefault;
	const validationPathState = data.validationPathState;

	if (!isExpanded) {
		return (
			<div
				className={css(
					styles.card,
					selected && styles.cardSelected,
					validationPathState === 'path' && styles.cardPath,
					validationPathState === 'target' && styles.cardTarget
				)}
			>
				<Handle type='target' position={Position.Top} id='target' className={css(styles.connectionDot)} />
				<div
					className={css(styles.compactInner)}
					onClick={handleToggleExpand}
					onKeyDown={event => {
						if (event.key === 'Enter' || event.key === ' ') {
							event.preventDefault();
							handleToggleExpand();
						}
					}}
					role='button'
					tabIndex={0}
				>
					<div className={css(styles.headerLeft)}>
						<div className={css(styles.iconCircle)}>
							<BehaviorIcon />
						</div>
						<span className={css(styles.compactTitle)}>{title}</span>
					</div>
					<div className={css(styles.headerActions)}>
						<button
							type='button'
							className={css(styles.iconButton)}
							onClick={event => {
								event.stopPropagation();
								setIsExpanded(true);
							}}
						>
							<ChevronIcon direction='down' />
						</button>
					</div>
				</div>
				<Handle type='source' position={Position.Bottom} id='default' className={css(styles.connectionDot)} />
			</div>
		);
	}

	return (
		<div
			className={`${css(
				styles.card,
				selected && styles.cardSelected,
				validationPathState === 'path' && styles.cardPath,
				validationPathState === 'target' && styles.cardTarget
			)} nopan nodrag`}
			onPointerDown={e => e.stopPropagation()}
		>
			<Handle type='target' position={Position.Top} id='target' className={css(styles.connectionDot)} />
			<div className={css(styles.header)}>
				<div className={css(styles.headerLeft)}>
					<div className={css(styles.iconCircle)}>
						<BehaviorIcon />
					</div>
					<h4 className={css(styles.title)}>{title}</h4>
				</div>
				<div className={css(styles.headerActions)}>
					<button
						type='button'
						className={css(styles.iconButton)}
						onClick={e => {
							e.stopPropagation();
							handleToggleExpand();
						}}
						aria-label='Collapse'
					>
						<ChevronIcon direction='up' />
					</button>
				</div>
			</div>
			<div className={css(styles.divider)} />
			<div className={css(styles.content)}>
				{isDefault && <p className={css(styles.defaultSubtitle)}>Default pathway for everyone else</p>}
			</div>
			<Handle type='source' position={Position.Bottom} id='default' className={css(styles.connectionDot)} />
		</div>
	);
}
